import { NYC_BOROUGHS, type BoroughSummary, type NycBorough } from '@vitals/shared';
import type { VitalView } from './vitals.js';

const BOROUGH_COLORS: Record<NycBorough, string> = {
  Bronx: '#ef5350',
  Brooklyn: '#ffb74d',
  Manhattan: '#66bb6a',
  Queens: '#42a5f5',
  'Staten Island': '#ab47bc',
};
const OUTSIDE_COLOR = '#78909c';

function isCount(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 0;
}

export function validBoroughSummary(summary: Record<string, unknown>): summary is BoroughSummary {
  if (!isCount(summary.count) || !isCount(summary.outsideBoroughs)) return false;
  if (!summary.boroughs || typeof summary.boroughs !== 'object' || Array.isArray(summary.boroughs)) return false;
  const boroughs = summary.boroughs as Record<string, unknown>;
  if (!NYC_BOROUGHS.every((borough) => isCount(boroughs[borough]))) return false;
  const classified = NYC_BOROUGHS.reduce((total, borough) => total + (boroughs[borough] as number), 0);
  return classified + summary.outsideBoroughs === summary.count;
}

export function boroughDetails(summary: BoroughSummary): NonNullable<VitalView['details']> {
  const classified = NYC_BOROUGHS.reduce((total, borough) => total + summary.boroughs[borough], 0);
  const rows = NYC_BOROUGHS
    .map((borough) => ({ borough, count: summary.boroughs[borough] }))
    .sort((a, b) => b.count - a.count || a.borough.localeCompare(b.borough));
  const maxCount = Math.max(0, ...rows.map(({ count }) => count));
  const details: NonNullable<VitalView['details']> = rows.map(({ borough, count }) => ({
    kind: 'category',
    label: borough,
    value: count.toLocaleString('en-US'),
    percent: `${classified > 0 ? Math.round(count / classified * 100) : 0}%`,
    frac: maxCount > 0 ? count / maxCount : 0,
    color: BOROUGH_COLORS[borough],
  }));

  if (summary.outsideBoroughs > 0) {
    details.push({
      kind: 'category',
      label: 'Outside borough boundaries',
      value: summary.outsideBoroughs.toLocaleString('en-US'),
      percent: `${summary.count > 0 ? Math.round(summary.outsideBoroughs / summary.count * 100) : 0}%`,
      frac: maxCount > 0 ? summary.outsideBoroughs / maxCount : 1,
      color: OUTSIDE_COLOR,
    });
  }
  return details;
}
